import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProducts, type Product } from "@/hooks/useProducts";
import ShopProductCard from "@/components/ShopProductCard";
import QuickViewModal from "@/components/QuickViewModal";
import { relevanceScore, MIN_RELEVANCE_SCORE } from "@/lib/keywordMatch";

interface RelatedProductsProps {
  postTitle: string;
  postExcerpt?: string | null;
  limit?: number;
}

function useBestSellerIds(limit: number) {
  return useQuery({
    queryKey: ["top-selling-ids", limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("top_selling_product_ids", { lim: limit });
      if (error) throw error;
      return (data ?? []) as number[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

export default function RelatedProducts({ postTitle, postExcerpt, limit = 3 }: RelatedProductsProps) {
  const { data: products = [] } = useProducts();
  const { data: bestSellerIds = [] } = useBestSellerIds(12);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);

  const matches = useMemo(() => {
    if (!products.length) return [] as Product[];

    const inStock = products.filter((p) => p.in_stock);
    const pool = inStock.length ? inStock : products;

    const scored = pool
      .map((p) => ({
        p,
        score: relevanceScore(
          { strong: [postTitle], weak: [postExcerpt] },
          { strong: [p.categories], weak: [p.name] }
        ),
      }))
      .filter((x) => x.score >= MIN_RELEVANCE_SCORE)
      .sort((a, b) => b.score - a.score);

    if (import.meta.env.DEV) {
      console.debug(
        "[RelatedProducts] top scores:",
        scored.slice(0, 5).map((x) => ({ name: x.p.name, score: x.score }))
      );
    }

    const real = scored.slice(0, limit).map((x) => x.p);
    if (real.length >= limit) return real;

    const picked = new Set(real.map((p) => p.id));

    // Fallback 1: best-sellers (in stock, not already picked).
    const byId = new Map(pool.map((p) => [p.id, p]));
    const bestSellers = bestSellerIds
      .map((id) => byId.get(id))
      .filter((p): p is Product => !!p && !picked.has(p.id));

    // Fallback 2: top-priced (featured proxy) for any remaining slot.
    const topPriced = [...pool]
      .filter((p) => !picked.has(p.id) && !bestSellers.some((b) => b.id === p.id))
      .sort((a, b) => (b.regular_price ?? 0) - (a.regular_price ?? 0));

    // Fallback 3: newest by id.
    const newest = [...pool]
      .filter((p) => !picked.has(p.id))
      .sort((a, b) => (b.id as number) - (a.id as number));

    return [...real, ...bestSellers, ...topPriced, ...newest].slice(0, limit);
  }, [products, bestSellerIds, postTitle, postExcerpt, limit]);

  if (matches.length === 0) return null;

  return (
    <section className="my-12 pt-10 border-t border-border">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            Shop the <span className="italic font-normal text-primary">post</span>
          </h2>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Hand-picked favourites that pair with this read.
          </p>
        </div>
        <Link
          to="/shop"
          className="hidden sm:inline-flex items-center gap-1 text-xs font-body font-medium uppercase tracking-[0.12em] text-primary hover:gap-2 transition-all whitespace-nowrap"
        >
          View all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-8">
        {matches.map((p, i) => (
          <ShopProductCard
            key={p.id}
            product={p}
            onQuickView={setQuickViewProduct}
            index={i}
          />
        ))}
      </div>

      <div className="sm:hidden mt-6 text-center">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1 text-xs font-body font-medium uppercase tracking-[0.12em] text-primary"
        >
          View all products <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        open={!!quickViewProduct}
        onOpenChange={(open) => !open && setQuickViewProduct(null)}
      />
    </section>
  );
}
